import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useSearchParams } from 'react-router-dom';
import SidePanel from '../components/Common/SidePanel';
import MobileMenuToggle from '../components/Common/MobileMenuToggle';
import ResponsiveTable from '../components/Common/ResponsiveTable';
import { Search, Eye, Edit2, Loader, AlertCircle, X, Save, Download } from 'lucide-react';
import { recordService } from '../services/api';

const Records = () => {
  const { user } = useContext(AuthContext);
  const { isCollapsed } = useSidebar();
  const [searchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [editError, setEditError] = useState('');
  const [userMap, setUserMap] = useState({});
  const [statusUpdateMode, setStatusUpdateMode] = useState(false);
  const [newStatus, setNewStatus] = useState(null);
  const [statusRemarks, setStatusRemarks] = useState('');

  useEffect(() => {
    fetchRecords();
  }, []);

  // Auto-open modal if recordId is in query params
  useEffect(() => {
    const recordId = searchParams.get('recordId');
    if (recordId && records.length > 0) {
      const record = records.find(r => r.id === recordId);
      if (record) {
        handleViewRecord(record);
      }
    }
  }, [searchParams, records]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await recordService.getAll();
      setRecords(response.data?.data?.records || []);
      
      // Fetch and map user data
      await fetchAndMapUsers(response.data?.data?.records || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const fetchAndMapUsers = async (recordsList) => {
    try {
      // Get all users to create a mapping of ID to fullName
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const users = data.data?.users || [];
        
        // Create mapping of userId -> fullName
        const map = {};
        users.forEach(u => {
          map[u.id] = u.fullName || u.email;
        });
        
        setUserMap(map);
      } else if (response.status === 403) {
        // Permission denied - user doesn't have access to /users endpoint
        // Try to extract unique user IDs from records and create a basic map
        // This is a fallback for non-admin users
        const uniqueUserIds = [...new Set(recordsList.map(r => r.lastUpdatedBy).filter(Boolean))];
        console.log('No permission to fetch all users, showing user IDs');
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      // Continue without user mapping - will show IDs instead of names
    }
  };

  let filteredRecords = records.filter(r =>
    r.barcodeNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.vehicleNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (statusFilter) {
    filteredRecords = filteredRecords.filter(r => r.status === statusFilter);
  }

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setIsEditMode(false);
    setEditData(null);
    setEditError('');
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setIsEditMode(true);
    setEditData({
      vehicleNo: record.vehicleNo || '',
      vehicleType: record.vehicleType || 'Car',
      notes: record.notes || ''
    });
    setEditError('');
  };

  const handleSaveRecord = async () => {
    if (!selectedRecord || !editData) return;

    setUpdating(true);
    setEditError('');

    try {
      await recordService.update(selectedRecord.id, editData);
      // Update local state
      setRecords(records.map(r => 
        r.id === selectedRecord.id 
          ? { ...r, ...editData }
          : r
      ));
      setSelectedRecord({ ...selectedRecord, ...editData });
      setIsEditMode(false);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedRecord || !newStatus) return;

    setUpdating(true);
    setEditError('');

    try {
      await recordService.updateStatus(selectedRecord.id, {
        status: newStatus,
        remarks: statusRemarks || null
      });

      // Update local state
      const updatedRecord = { ...selectedRecord, status: newStatus };
      setRecords(records.map(r => 
        r.id === selectedRecord.id 
          ? updatedRecord
          : r
      ));
      setSelectedRecord(updatedRecord);
      setStatusUpdateMode(false);
      setNewStatus(null);
      setStatusRemarks('');
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const closeModal = () => {
    setSelectedRecord(null);
    setIsEditMode(false);
    setEditData(null);
    setStatusUpdateMode(false);
    setNewStatus(null);
    setStatusRemarks('');
    setEditError('');
  };

  const exportToExcel = () => {
    if (filteredRecords.length === 0) {
      alert('No records to export');
      return;
    }

    // Prepare data for Excel
    const headers = ['Barcode Number', 'Vehicle Number', 'Vehicle Type', 'Status', 'Modified By', 'Created Date', 'Updated Date', 'Notes'];
    
    // Map records to rows
    const rows = filteredRecords.map(record => [
      record.barcodeNumber,
      record.vehicleNo,
      record.vehicleType || 'N/A',
      record.status,
      userMap[record.lastUpdatedBy] || record.lastUpdatedBy || 'N/A',
      new Date(record.createdAt).toLocaleString(),
      record.updatedAt ? new Date(record.updatedAt).toLocaleString() : 'N/A',
      record.notes || ''
    ]);

    // Create CSV content
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.map(h => `"${h}"`).join(',') + '\n';
    csvContent += rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    // Create blob and download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `service-records-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
        <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <MobileMenuToggle onClick={() => setIsSidebarOpen(true)} isSidebarOpen={isSidebarOpen} />
      <SidePanel isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        <main className="flex-1 overflow-y-auto pt-28 md:pt-0">
          <div className="p-6 md:p-8 max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Service Records</h1>
              <p className="text-slate-600">View and manage service records</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col md:flex-row gap-4 flex-1 w-full md:w-auto">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search records..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="ASSISTED">Assisted</option>
                    <option value="HOLD">Hold</option>
                    <option value="RETURN">Return</option>
                  </select>
                </div>
                <button
                  onClick={exportToExcel}
                  className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg font-semibold transition shadow-md flex items-center gap-2 whitespace-nowrap"
                >
                  <Download className="w-5 h-5" />
                  Export to Excel
                </button>
              </div>

              <ResponsiveTable
                columns={user?.role === 'ADMIN' ? [
                  { key: 'createdAt', label: 'Date' },
                  { key: 'barcodeNumber', label: 'Barcode' },
                  { key: 'vehicleNo', label: 'Vehicle' },
                  { key: 'vehicleType', label: 'Type' },
                  { key: 'notes', label: 'Note' },
                  { key: 'modifiedBy', label: 'Modified by' },
                  { key: 'status', label: 'Status' },
                  { key: 'actions', label: 'Actions' }
                ] : [
                  { key: 'createdAt', label: 'Date' },
                  { key: 'barcodeNumber', label: 'Barcode' },
                  { key: 'vehicleNo', label: 'Vehicle' },
                  { key: 'notes', label: 'Note' },
                  { key: 'status', label: 'Status' },
                  { key: 'actions', label: 'Actions' }
                ]}
                data={filteredRecords}
                renderRow={(r, idx, isMobileCard) => (
                  isMobileCard ? (
                    <div key={r.id} className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-xs text-slate-500 font-semibold uppercase">Date</p>
                          <p className="text-sm font-semibold text-slate-900">{new Date(r.createdAt).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                          r.status === 'PENDING' ? 'bg-blue-100 text-blue-800' :
                          r.status === 'ASSISTED' ? 'bg-green-100 text-green-800' :
                          r.status === 'HOLD' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {r.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-semibold uppercase">Barcode</p>
                        <p className="text-sm font-medium text-slate-700">{r.barcodeNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 font-semibold uppercase">Vehicle</p>
                        <p className="text-sm font-medium text-slate-700">{r.vehicleNo}</p>
                      </div>
                      {user?.role === 'ADMIN' && (
                        <>
                          <div>
                            <p className="text-xs text-slate-500 font-semibold uppercase">Type</p>
                            <p className="text-sm font-medium text-slate-700">{r.vehicleType || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 font-semibold uppercase">Modified by</p>
                            <p className="text-sm font-medium text-slate-700">{userMap[r.lastUpdatedBy] || r.lastUpdatedBy || '-'}</p>
                          </div>
                        </>
                      )}
                      {r.notes && (
                        <div>
                          <p className="text-xs text-slate-500 font-semibold uppercase">Notes</p>
                          <p className="text-sm text-slate-700">{r.notes}</p>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2 border-t border-slate-100">
                        <button 
                          onClick={() => handleViewRecord(r)}
                          className="flex-1 px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg transition text-sm font-medium flex items-center justify-center gap-2" 
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        {user?.role === 'ADMIN' && (
                          <button 
                            onClick={() => handleEditRecord(r)}
                            className="flex-1 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition text-sm font-medium flex items-center justify-center gap-2" 
                            title="Edit Record"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <tr key={r.id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="px-6 py-4 text-slate-600">{new Date(r.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4 font-medium">{r.barcodeNumber}</td>
                      <td className="px-6 py-4">{r.vehicleNo}</td>
                      {user?.role === 'ADMIN' && (
                        <>
                          <td className="px-6 py-4">{r.vehicleType || '-'}</td>
                          <td className="px-6 py-4 text-slate-700">{r.notes || '-'}</td>
                          <td className="px-6 py-4">{userMap[r.lastUpdatedBy] || r.lastUpdatedBy || '-'}</td>
                        </>
                      )}
                      {user?.role !== 'ADMIN' && (
                        <td className="px-6 py-4 text-slate-700">{r.notes || '-'}</td>
                      )}
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          r.status === 'PENDING' ? 'bg-blue-100 text-blue-800' :
                          r.status === 'ASSISTED' ? 'bg-green-100 text-green-800' :
                          r.status === 'HOLD' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right flex justify-end gap-2">
                        <button 
                          onClick={() => handleViewRecord(r)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition" 
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                        {user?.role === 'ADMIN' && (
                          <button 
                            onClick={() => handleEditRecord(r)}
                            className="p-2 hover:bg-blue-100 rounded-lg transition" 
                            title="Edit Record"
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                )}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Record Detail Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Record Details</h2>
                <p className="text-blue-100 text-sm mt-1">Barcode: {selectedRecord.barcodeNumber}</p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-blue-500 rounded-lg transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8 space-y-6">
              {editError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-800">{editError}</p>
                </div>
              )}

              {!isEditMode ? (
                /* View Mode */
                <div className="space-y-6">
                  {/* Status Badge */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-300">
                    <p className="text-xs text-blue-600 font-bold uppercase mb-2">Status</p>
                    <p className={`text-2xl font-bold ${
                      selectedRecord.status === 'PENDING' ? 'text-blue-600' :
                      selectedRecord.status === 'ASSISTED' ? 'text-green-600' :
                      selectedRecord.status === 'HOLD' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {selectedRecord.status}
                    </p>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-slate-600 font-bold uppercase mb-1">Barcode Number</p>
                      <p className="text-lg font-semibold text-slate-900">{selectedRecord.barcodeNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 font-bold uppercase mb-1">Vehicle Number</p>
                      <p className="text-lg font-semibold text-slate-900">{selectedRecord.vehicleNo || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 font-bold uppercase mb-1">Vehicle Type</p>
                      <p className="text-lg font-semibold text-slate-900">{selectedRecord.vehicleType || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 font-bold uppercase mb-1">Date of Entry</p>
                      <p className="text-lg font-semibold text-slate-900">{selectedRecord.dateOfEntry || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 font-bold uppercase mb-1">Time of Entry</p>
                      <p className="text-lg font-semibold text-slate-900">{selectedRecord.timeOfEntry || '-'}</p>
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedRecord.notes && (
                    <div>
                      <p className="text-xs text-slate-600 font-bold uppercase mb-2">Notes</p>
                      <p className="text-slate-700 bg-slate-50 p-4 rounded-lg">{selectedRecord.notes}</p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="pt-4 border-t border-slate-200 space-y-2 text-sm text-slate-600">
                    <p>Created: {new Date(selectedRecord.createdAt).toLocaleString()}</p>
                    {selectedRecord.updatedAt && (
                      <p>Last Updated: {new Date(selectedRecord.updatedAt).toLocaleString()}</p>
                    )}
                  </div>

                  {/* Edit Button */}
                  {user?.role === 'ADMIN' && (
                    <button
                      onClick={() => handleEditRecord(selectedRecord)}
                      className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition font-semibold"
                    >
                      <Edit2 className="w-5 h-5" />
                      Edit Record
                    </button>
                  )}

                  {/* Status Update Section for SERVICE_ENGINEER */}
                  {user?.role === 'SERVICE_ENGINEER' && (
                    <>
                      {!statusUpdateMode ? (
                        <button
                          onClick={() => {
                            setStatusUpdateMode(true);
                            setNewStatus(selectedRecord.status);
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 rounded-lg hover:from-purple-700 hover:to-purple-800 transition font-semibold"
                        >
                          <Edit2 className="w-5 h-5" />
                          Update Status
                        </button>
                      ) : (
                        <div className="bg-purple-50 p-6 rounded-lg border-2 border-purple-200 space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">New Status</label>
                            <select
                              value={newStatus}
                              onChange={(e) => setNewStatus(e.target.value)}
                              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="">Select Status</option>
                              <option value="ASSISTED">Assisted</option>
                              <option value="HOLD">Hold</option>
                              <option value="RETURN">Return</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Remarks (Optional)</label>
                            <textarea
                              rows={3}
                              value={statusRemarks}
                              onChange={(e) => setStatusRemarks(e.target.value)}
                              placeholder="Add any remarks about the status change..."
                              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={handleUpdateStatus}
                              disabled={updating || !newStatus}
                              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2 rounded-lg hover:from-green-700 hover:to-emerald-700 transition font-semibold disabled:from-slate-400 disabled:to-slate-500"
                            >
                              {updating ? (
                                <>
                                  <Loader className="w-4 h-4 animate-spin" />
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <Save className="w-4 h-4" />
                                  Update
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setStatusUpdateMode(false);
                                setNewStatus(null);
                                setStatusRemarks('');
                              }}
                              disabled={updating}
                              className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition font-semibold disabled:bg-slate-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                /* Edit Mode */
                <form onSubmit={(e) => { e.preventDefault(); handleSaveRecord(); }} className="space-y-6">
                  {/* Read-only Reference Fields */}
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
                    <p className="text-xs text-slate-600 font-bold uppercase">Reference Information (Read-only)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Status</p>
                        <p className={`font-semibold ${
                          selectedRecord.status === 'PENDING' ? 'text-blue-600' :
                          selectedRecord.status === 'ASSISTED' ? 'text-green-600' :
                          selectedRecord.status === 'HOLD' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {selectedRecord.status}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Date of Entry</p>
                        <p className="font-semibold text-slate-900">{selectedRecord.dateOfEntry}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1\">Time of Entry</p>
                        <p className="font-semibold text-slate-900">{selectedRecord.timeOfEntry}</p>
                      </div>
                    </div>
                  </div>

                  {/* Editable Fields */}
                  <div className="border-t border-slate-200 pt-6">
                    <p className="text-xs text-slate-600 font-bold uppercase mb-4">Edit Information</p>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicle Number</label>
                      <input
                        type="text"
                        value={editData.vehicleNo}
                        onChange={(e) => setEditData({...editData, vehicleNo: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., CG02MY8560"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Vehicle Type</label>
                      <select
                        value={editData.vehicleType}
                        onChange={(e) => setEditData({...editData, vehicleType: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Type 1">Type 1</option>
                        <option value="Type 2">Type 2</option>
                        <option value="Type 3">Type 3</option>
                      </select>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Notes</label>
                      <textarea
                        rows={4}
                        value={editData.notes}
                        onChange={(e) => setEditData({...editData, notes: e.target.value})}
                        placeholder="Add or update notes..."
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-slate-200">
                    <button
                      type="submit"
                      disabled={updating}
                      className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 transition font-semibold disabled:from-slate-400 disabled:to-slate-500"
                    >
                      {updating ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditMode(false)}
                      disabled={updating}
                      className="flex-1 flex items-center justify-center gap-2 bg-slate-600 text-white py-3 rounded-lg hover:bg-slate-700 transition font-semibold disabled:bg-slate-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Records;
